import { GoTrueClient } from '@supabase/auth-js'
import { Capacitor } from '@capacitor/core'
import { GoogleAuth } from '@southdevs/capacitor-google-auth'
import { markSignoutSelfInitiated } from './authDiag'
import { durableSessionStorage } from './durableSessionStorage'
import { resetPostHog } from './posthog'
import { QUERY_CACHE_KEY, forgetCacheOwner } from './queryPersist'
import { unregisterPushBeforeSignOut } from './push'
import { forgetSessionIdentity } from './sessionIdentity'

// Resolve the GoTrue URL with a tiered fallback. Build-time env var wins;
// otherwise, on a native (Capacitor) build we MUST default to the
// production GoTrue rather than localhost — the device can't reach
// http://localhost:9999, so a missing env var on iOS used to surface as
// "Network Error" on the login screen with no further diagnostic.
// (This was the TestFlight-build-13 sign-in bug — `apps/web/.env.production`
// wasn't committed, the Fastfile build produced a bundle with no
// VITE_GOTRUE_URL, and the original `?? 'http://localhost:9999'` fallback
// shipped to every phone.) On the web we still default to localhost
// because that's actually where dev developers run GoTrue.
function resolveGoTrueUrl(): string {
  const fromEnv = import.meta.env.VITE_GOTRUE_URL
  if (fromEnv) return fromEnv
  if (Capacitor.isNativePlatform()) return 'https://auth.trybeup.com'
  return 'http://localhost:9999'
}

// No-op lock for GoTrueClient. The library's default `navigatorLock`
// uses `navigator.locks.request` with a hardcoded 5000ms acquire
// timeout (see node_modules/@supabase/auth-js/dist/main/GoTrueClient.js
// line 28: `lockAcquireTimeout: 5000`). On a cold WKWebView start the
// LockManager state is uninitialised and the very first request times
// out deterministically — which we observed as a flat ~5024ms in the
// react_render→session_ready segment of the founder's boot-timing chip,
// repeated to the millisecond across multiple cold opens (1.1.215 +
// 1.1.216). The library exports `lockNoOp` for exactly this case; the
// lock exists to coordinate token refreshes across BROWSER TABS, and
// inside Capacitor's single WebView there is no second tab to
// coordinate with — locking is pure overhead. Web users keep the
// default lock so cross-tab safety isn't sacrificed for them. Founder
// cold-start fix, 2026-06-14.
const noOpLock = async <R>(
  _name: string,
  _acquireTimeout: number,
  fn: () => Promise<R>,
): Promise<R> => fn()

export const authClient = new GoTrueClient({
  url: resolveGoTrueUrl(),
  storageKey: 'trybeup_session',
  // Hybrid localStorage + IndexedDB adapter. Survives iOS Safari's
  // memory-pressure eviction and ITP cleanup of localStorage, which
  // was the recurring root cause of the "Safari mobile signed me out
  // again" reports after the warmer retry + provider-gate + idle
  // heartbeat fixes were already in place. See
  // apps/web/src/lib/durableSessionStorage.ts for the full rationale.
  storage: durableSessionStorage,
  // Native-only — see noOpLock comment above. Web keeps the default
  // cross-tab navigator-locks behaviour.
  ...(Capacitor.isNativePlatform() ? { lock: noOpLock } : {}),
  // Disabled deliberately. With autoRefreshToken: true, the library runs an
  // internal timer that refreshes the access token every ~1h. If THAT call
  // fails with anything that isn't a retryable fetch error (i.e. anything
  // other than a network drop or 5xx), the library calls its private
  // `_removeSession()` and emits `SIGNED_OUT` — silently logging the user
  // out. Common triggers: a 400 invalid_grant during a refresh-token
  // rotation race between tabs, a brief 4xx during a deploy window, etc.
  //
  // Our axios interceptor in lib/api.ts already refreshes on 401 with a
  // 5xx-tolerant outcome (see `tryRefreshSession`), so we don't need the
  // library's timer. Refreshing only when an API call actually returns 401
  // is sufficient and removes the silent-signout vector entirely.
  autoRefreshToken: false,
  persistSession: true,
  detectSessionInUrl: true,
})

export async function getAccessToken(): Promise<string | null> {
  const { data } = await authClient.getSession()
  return data.session?.access_token ?? null
}

export async function signOut(reason: string = 'user-initiated') {
  // Mark this sign-out as "we did this on purpose" so the SIGNED_OUT
  // auth-state-change listener can tell expected sign-outs apart from
  // ones the SDK fires internally after a failed refresh — that
  // distinction is exactly what the Safari mobile sign-out diagnostics
  // need.
  markSignoutSelfInitiated(reason)

  // Forget the PostHog identity NOW, synchronously. Every caller (Profile's
  // Log Out, the 401 handler, the "remember me off" idle sign-out) navigates
  // away right after this resolves, so the useAuth effect that used to do the
  // reset never ran and the device stayed identified as the previous person
  // (review of 1.3.156). resetPostHog is a no-op without a key and never
  // throws, so a missing/unloaded PostHog can't block sign-out.
  resetPostHog()

  clearPerUserStorage()
  // The next sign-in (same person or not) must re-register push and
  // RevenueCat — see lib/sessionIdentity.ts.
  forgetSessionIdentity()

  // Unregister this device's push WHILE the session is still valid. It used to
  // run from useAuth's SIGNED_OUT handler, after GoTrue had already deleted the
  // session — the DELETE went out with no token and 401'd, so a signed-out
  // phone kept getting the previous person's reminders (review 2026-10-17).
  // Skipped when the token has already expired (the reactive-401 / warmer
  // sign-outs: it would only 401 again). Bounded by a short timeout; never
  // blocks or fails the sign-out.
  if (Capacitor.isNativePlatform()) {
    try {
      // Read the STORED session — getSession() refreshes an expired token
      // and retries for ~30 s offline, which doubled an offline sign-out
      // (review 2026-10-17). An expired token is skipped below anyway.
      let sess: { access_token?: string; expires_at?: number } | null = null
      try { sess = JSON.parse(localStorage.getItem('trybeup_session') || 'null') } catch { sess = null }
      const now = Math.floor(Date.now() / 1000)
      const live = !!sess?.access_token && (!sess.expires_at || sess.expires_at > now)
      if (live) await unregisterPushBeforeSignOut(sess!.access_token ?? null)
    } catch {
      // storage hiccup — the backend releases the id when someone else
      // registers this device, so this is never the only safety net
    }
  }

  // On native iOS / Android we also need to clear the Google Sign-In SDK's
  // cached account, otherwise the next "Continue with Google" tap silently
  // re-uses the previously signed-in Google account without showing the
  // account picker — which prevents switching between Google accounts.
  // Best-effort: never let a Google SDK error block GoTrue sign-out.
  if (Capacitor.isNativePlatform()) {
    try {
      await GoogleAuth.signOut()
    } catch {
      // ignore — user may not have signed in via Google on this device
    }
  }

  try {
    await authClient.signOut()
  } catch {
    // GoTrue's global sign-out returns early on a network/5xx error WITHOUT
    // dropping the local session. Fall back to a local sign-out so this
    // device is signed out either way (review 2026-09-13).
    await authClient.signOut({ scope: 'local' }).catch(() => {})
  }

  // Again, AFTER the network call: the query cache saves on a 1s throttle, and
  // a save queued before the removal above can land while GoTrue's request is
  // in flight — the session is still on disk then, so it writes this
  // account's data straight back (review 2026-10-13). The blob is also bound
  // to its owner (lib/queryPersist.ts); this just keeps it off the disk.
  try { localStorage.removeItem(QUERY_CACHE_KEY) } catch { /* storage blocked */ }
  // That queued save also re-claimed the in-memory owner for this account.
  forgetCacheOwner()
}

/**
 * Remove everything on this device that belongs to the signed-in account.
 * signOut() runs it, and so does useAuth's SDK-confirmed sign-out (a
 * SIGNED_OUT we didn't start, verified as a real session loss) — that path
 * used to clear only the in-memory query cache, leaving the saved cache, the
 * offline queues and workout state for the next person (review 2026-10-13).
 * Idempotent.
 */
export function clearPerUserStorage() {
  // Clear per-user localStorage so a new user on the same device gets a clean
  // slate (e.g. onboarding state from the previous account).
  localStorage.removeItem('trybeup_onboarding_complete')
  localStorage.removeItem('trybeup_onboarding_user')
  localStorage.removeItem('trybeup_session_temporary')
  // The completed-steps counter gates the reminders offer and the "get the
  // app" banner ("never the first thing a new account sees") — it belongs to
  // the account, not the device (review of 1.3.160). Same key as
  // lib/notificationOptIn COMPLETIONS_KEY.
  localStorage.removeItem('trybeup_total_completions')
  localStorage.removeItem('trybeup_last_active')
  // Added 2026-06-12 with the coach-conversation persistence shipped
  // in 1.1.175 — without this, a shared device that signs out and back
  // in as a different user would land the new account on the previous
  // user's conversation ID. The messages query 404/403 recovery in
  // MyCoach handles the next step gracefully, but clearing here means
  // the new user never sees the broken-chat error state at all.
  localStorage.removeItem('trybeup_coach_active_conv')
  // Added 2026-06-20 with the Focus Mode feature. Both records are
  // per-user (focus session contains the previous account's habit
  // id; pending-capgo-apply is a per-device update marker but
  // dropping it on sign-out is harmless — it just defers the post-
  // launch '✓ Updated' toast for one cycle). Without these, a
  // family-shared device that signs out and back in as a different
  // user could restore the previous account's prayer timer and try
  // to log it against the new user → 404 'Couldn't log the session'.
  localStorage.removeItem('trybeup_focus_session')
  localStorage.removeItem('trybeup_capgo_pending_apply')
  // The Simple-workout and Recovery timers feed the same "Workout in progress"
  // pill as trybeup_active_workout and record no owner — the next person saw
  // the last one's timer and could log it to their own account. The problem
  // typed on the first screen is someone's own words (14-day TTL) and was
  // shown to, and started a plan for, the next person (review 2026-10-21).
  localStorage.removeItem('trybeup_simple_workout_draft')
  localStorage.removeItem('trybeup_recovery_timer_draft')
  localStorage.removeItem('trybeup_pending_pilot')
  // Cached premium flag (useSubscription's first-paint fallback). Without this,
  // a free user signing in after a premium user on a shared device sees
  // premium-only UI until their /api/subscription fetch settles — a confusing
  // flash and a mild leak of the prior user's status. Every real gate is
  // server-side so it's cosmetic, but clear it anyway (audit 2026-07-19).
  localStorage.removeItem('trybeup:isPremium')
  localStorage.removeItem('trybeup:hasPaidPremium')
  // The persisted query cache and the offline queues are per-account too:
  // a hard navigation right after sign-out beat the 1s persist throttle, so
  // the next sign-in on a shared phone first painted the previous person's
  // profile and habits, and replayed their queued taps (review 2026-09-13).
  localStorage.removeItem(QUERY_CACHE_KEY)
  forgetCacheOwner()
  localStorage.removeItem('trybeup_offline_queue')
  localStorage.removeItem('trybeup_offline_fallback_queue')
  // Workout state is per account too: the next person saw the previous
  // one's "Workout in progress" pill and could restore their draft.
  localStorage.removeItem('trybeup_active_workout')
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i)
      if (k && k.startsWith('trybeup_workout_snapshot')) localStorage.removeItem(k)
    }
  } catch { /* ignore */ }
  try {
    if (typeof indexedDB !== 'undefined') indexedDB.deleteDatabase('trybeup_offline')
  } catch { /* best-effort */ }
}
